import { CART_ITEM_TYPES } from '../constants/web-gallery-cart-constants';
import { allowsDownloadWatermarkOption } from '../../../util/album';

export const TAX = 0.085; // 8.5%

/**
 * Computes the total price of a cart item based on its quantity.
 *
 * @param {CartItem} cartItem The item to calculate the price for.
 */
const getCartItemTotalPrice = cartItem => {
  return cartItem.type !== CART_ITEM_TYPES.PRINT
    ? cartItem.priceInfo.value
    : cartItem.priceInfo.value * cartItem.quantity;
};

/**
 * Returns the sum of a single cart.
 *
 * @param {Array} purchaseModeCart An array containing cartItems (either Downloads or Prints)
 * @returns {Number} The sum of all items in cart.
 */
const getPurchaseModeCartTotal = (purchaseModeCart = []) => {
  return purchaseModeCart.reduce((sum, cartItem) => {
    return sum + getCartItemTotalPrice(cartItem);
  }, 0);
};

const getTax = (cartTotal, address) => {
  if (address && address.state && address.state.match(/texas|tx/i)) {
    return Math.round(cartTotal * TAX);
  }

  return 0;
};

/**
 * Gets overall total of the current cart status.
 *
 * @param {Array<CartItem>} selectedDownloads Selected downloads cart.
 * @param {Array<CartItem>} selectedPrints Selected prints cart.
 * @param {Address} address Address prints are shipping to.
 * @param {Shipping} shipping Shipping costs.
 * @returns {Number} Total amount due for the current cart.
 */
const getFinalTotal = (
  selectedDownloads,
  selectedPrints,
  address,
  shipping,
) => {
  const downloadsCartTotal = getPurchaseModeCartTotal(selectedDownloads);
  const printCartTotal = getPurchaseModeCartTotal(selectedPrints);

  const shippingCosts =
    (selectedPrints?.length ?? 0) > 0 ? shipping?.value ?? 0 : 0;

  const finalPrintCartTotal =
    printCartTotal +
    shippingCosts +
    getTax(printCartTotal + shippingCosts, address);

  return finalPrintCartTotal + downloadsCartTotal;
};

/**
 * Confirms if a photo item is selected in a certain cart.
 *
 * @param {Array<CartItem>} cart Be it the download or print cart object.
 * @param {Photo} photoItem Photo to look for in the cart.
 * @param {Album} album The album the photo belongs to..
 * @param {PrintSize} [printSize] If on the print cart, the pintSize used to check against.
 * @returns {Boolean} If an item is selected in cart based on its photoID & albumID.
 */
const isSelectedInCart = (cart, photoItem, album, printSize) => {
  if (!cart) {
    return false;
  }

  if (printSize) {
    return !!cart.find(
      cartItem =>
        cartItem.photoItem.id === photoItem.id &&
        cartItem.album.id === album.id &&
        printSize.id === cartItem.printSize.id,
    );
  }

  return !!cart.find(
    cartItem =>
      cartItem.photoItem.id === photoItem.id && cartItem.album.id === album.id,
  );
};

/**
 * Checks whether a photo has already been purchased for download.
 * The purchasedDownloads array is supposed to be optional as the
 * main source of actual purchases should be the album.
 *
 * @param {Object<Album>} album Album object from GraphQL.
 * @param {Object<Photo>} photoItem A photo object from GraphQL containing Id.
 * @param {Array<String>?} purchasedDownloads An array of Photo Ids that have been purchased.
 */
const isPhotoDownloadPurchased = (album, photoItem, purchasedDownloads) => {
  const purchasedItems = album?.purchased_photo_downloads?.records ?? [];

  return (
    !!purchasedItems.find(
      purchasedItem => purchasedItem.photo.id === photoItem.id,
    ) ||
    (purchasedDownloads &&
      !!purchasedDownloads.find(
        purchasedItem =>
          purchasedItem.photoId === photoItem.id &&
          purchasedItem.albumId === album.id,
      ))
  );
};

/**
 * Given a list of selected prints, makes them into proper
 * `PurchasedPhotoPrint` Objects so that they can be used in
 * checkout or shipping query mutations.
 *
 * @param {Array<Object>} selectedPrints The printItems from the reducer.
 */
const selectedPrintsToPurchasedPhotoPrint = selectedPrints => {
  return selectedPrints.map(printItem => ({
    photo_id: printItem.photoItem.id,
    album_id: printItem.album.id,
    photo_print_size_id: printItem.printSize.id,
    quantity: printItem.quantity,
    print_without_watermark: !printItem.watermarkOption,
  }));
};

/**
 * Given a list of selected downloads, makes them into proper
 * `photo_download_items` Objects so that they can be used in
 * checkout or shipping query mutations.
 *
 * @param {Array<Object>} selectedDownloads The download items from the reducer.
 */
const selectedDownloadsToPhotoDownloadItem = selectedDownloads => {
  return selectedDownloads.map(downloadItem => ({
    photo_id: downloadItem.photoItem.id,
    album_id: downloadItem.album.id,
  }));
};

/**
 * Given an input, returns an object
 * ready to be used in the checkout mutation.
 *
 * @param {String} accountId Authenticated user account id.
 * @param {String} addressId Out of the different user address, the one selected by the user.
 * @param {Array<CartItem>} selectedDownloads Downloads cart.
 * @param {Array<CartItems>} selectedPrints Prints cart.
 * @param {String} stripeToken Stripe token returned after transaction has been completed.
 * @param {String} emailAddress Email address of the user purchasing the photos.
 */
const prepareCartForMutation = (
  accountId,
  addressId,
  selectedDownloads,
  selectedPrints,
  stripeToken,
  emailAddress,
) => {
  return {
    account_id: accountId,
    account_address_id: addressId,
    photo_print_items: selectedPrintsToPurchasedPhotoPrint(selectedPrints),
    photo_download_items: selectedDownloadsToPhotoDownloadItem(
      selectedDownloads,
    ),
    stripe_token: stripeToken,
    email_address: emailAddress,
  };
};

/**
 * Gets the total number of items currently in a cart.
 *
 * @param {Array<CartItem>} cart An array of CartItems for which we'll get the total quantity.
 * @returns {number} The quantity of items in the cart.
 */
const getCartTotalItems = cart => {
  return cart.reduce((sum, cartItem) => {
    return sum + (cartItem.quantity || 1);
  }, 0);
};

const getDownloadStorePhotoItem = (photoItem, album) => {
  return {
    photoItem,
    priceInfo: album.web_gallery_price_per_photo,
    album: {
      id: album.id,
      name: album.name,
      allowsWatermarkOption: allowsDownloadWatermarkOption(album),
    },
  };
};

/**
 * Returns an object (if found) that has been selected in the cart.
 *
 * @param {Array<CartItem>} cart Be it the download or print cart object.
 * @param {Photo} photoItem Photo to look for in the cart.
 * @param {Album} album The album the photo belongs to..
 * @param {PrintSize} [printSize] If on the print cart, the pintSize used to check against.
 * @returns {Boolean} If an item is selected in cart based on its photoID & albumID.
 */
const getCartItem = (cart, photoItem, album, printSize) => {
  if (!cart) {
    return null;
  }

  if (printSize) {
    return cart.find(
      cartItem =>
        cartItem.photoItem.id === photoItem.id &&
        cartItem.album.id === album.id &&
        printSize.id === cartItem.printSize.id,
    );
  }

  return cart.find(
    cartItem =>
      cartItem.photoItem.id === photoItem.id && cartItem.album.id === album.id,
  );
};

export {
  getCartItemTotalPrice,
  getPurchaseModeCartTotal,
  isSelectedInCart,
  isPhotoDownloadPurchased,
  prepareCartForMutation,
  getFinalTotal,
  getTax,
  getCartTotalItems,
  getDownloadStorePhotoItem,
  selectedPrintsToPurchasedPhotoPrint,
  selectedDownloadsToPhotoDownloadItem,
  getCartItem,
};
